import { TranslateService } from "@ngx-translate/core";
import moment from "moment";
import { HiLevelDataFormat } from "src/components/shared-components/enum/enumAddress";
import { TimeRelatedKeywords } from "src/components/shared-components/enum/enumMisc";
import { TranslationService } from "src/service/translation.service";
import { Address } from "../../../components/shared-components/class/component-classes/address";
import { EnumStringConverter } from "../../../components/shared-components/enum/enum-string-converter";
import { Chart } from "./chart";
import { Scenario } from "./scenario";
import { Supervision } from "./supervision";

/**
 * This class handle keyword conversion for BIGStudio ecosystem
 */
export class KeywordTextFormatterAdapter {
  private _currentDateTimeKeywordUsed: Array<string> = null;
  private _timeRelatedKeywords = TimeRelatedKeywords;
  private _originalText: string = "";
  private _defaultDecimailDigits: number = 0;
  private _useThousandSeparator: boolean = false;
  private _translatioNService: TranslationService = null;
  private _containDateTimeKeyword: boolean = false;

  /**
   * Getter who indicate if original text contain date time keyword
   */
  public get containDateTimeKeyword() {
    return this._containDateTimeKeyword;
  }

  constructor(
    originalTextString: string,
    defaultDecimalDigits: number,
    useThousandSeparator: boolean,
    translationService: TranslationService
  ) {
    this._translatioNService = translationService;
    this._originalText = originalTextString;

    this._defaultDecimailDigits = defaultDecimalDigits;
    this._useThousandSeparator = useThousandSeparator;

    this._containDateTimeKeyword =
      this.valueContainsDateTimeKeywords(originalTextString);

    if (this._containDateTimeKeyword) {
      this.loadDateTimeKeywordsKey();
    }
  }

  /**
   * Check if generic string have inside translated text.
   * If true, then replace translation with specific translated text.
   * Otherwiser return strings
   *
   * @param text
   * @returns
   */
  protected handleTextTranslation(text: string): string {
    if (this.ValueContainsTranslateData(text)) {
      text = this._translatioNService.TranslateText(text);
    }

    return text;
  }

  /**
   * Handle data change event for gauge
   */
  protected DataChangeGauge(): void { }

  private ValueContainsTranslateData(value: string) {
    return value.includes("{{") && value.includes("}}");
  }

  /**
   * Generic replace text function.
   * It replace text based on address object value, scenario object and supervision object
   *
   * @param currentAddress Address to handle
   * @param currentScenario Scenario to handle
   * @param currentSupervison Supervision to handle
   * @returns
   */
  public replaceText(
    currentAddress: Address,
    currentScenario: Scenario,
    currentSupervison: Supervision,
    currentChart: Chart
  ): string {
    let returnText = this._originalText;

    if (currentScenario) {
      returnText = this.replaceScenarioKeywordText(currentScenario, returnText);
    }

    if (currentSupervison) {
      returnText = this.replaceSupervisionKeywordText(
        currentSupervison,
        returnText
      );
    }

    if (currentAddress) {
      returnText = this.replacecAddressKeywordText(currentAddress, returnText);
    }

    if (currentChart) {
      returnText = this.replaceChartKeywordText(currentChart, returnText);
    }

    if (this._containDateTimeKeyword) {
      returnText = this.replaceDateTimeKeywords(returnText);
    }

    returnText = this.handleTextTranslation(returnText);

    return returnText;
  }

  /**
   * Replace string keyword by scenario object property
   *
   * @param scenario Scenario object
   * @param currentText Starting text to replace keywords
   * @returns
   */
  private replaceScenarioKeywordText(
    scenario: Scenario,
    currentText: string
  ): string {
    let returnText: string = currentText;

    returnText = returnText.replace("#name", scenario.name);

    returnText = this.checkTrimKeyword(returnText);

    return returnText;
  }

  /**
   * Replace string keyword by chart object property
   *
   * @param chart chart object
   * @param currentText Starting text to replace keywords
   * @returns
   */
  private replaceChartKeywordText(chart: Chart, currentText: string): string {
    let returnText: string = currentText;

    returnText = returnText.replace("#name", chart.name);
    returnText = returnText.replace("#description", chart.description);

    return returnText;
  }

  /**
   * Replace string keyword by supervision object property
   *
   * @param supervision Supervision object
   * @param currentText Starting text to replace keywords
   * @returns
   */
  private replaceSupervisionKeywordText(
    supervision: Supervision,
    currentText: string
  ): string {
    let returnText: string = currentText;

    returnText = returnText.replace("#name", supervision.name);

    return returnText;
  }

  /**
   * Replace string keyword by address object property
   *
   * @param address Address object
   * @param currentText Starting text to replace keywords
   * @returns
   */
  private replacecAddressKeywordText(
    address: Address,
    currentText: string
  ): string {
    let returnText: string = currentText;
    let decimalDigits = -1;

    returnText = returnText.replace("#address", address.name);

    const indexOfDecimalDigits = returnText.indexOf("#decimaldigits");
    if (indexOfDecimalDigits >= 0) {
      decimalDigits = parseInt(
        returnText.substring(
          indexOfDecimalDigits + 14,
          indexOfDecimalDigits + 15
        ),
        10
      );

      returnText =
        returnText.substring(0, indexOfDecimalDigits) +
        returnText.substring(indexOfDecimalDigits + 15, returnText.length);
    }

    returnText = returnText.replace("#name", address.name);
    returnText = returnText.replace("#fullname", address.fullName);
    returnText = returnText.replace("#datatype", address.hiLevelDataFormat);
    returnText = returnText.replace("#hileveldataformat", address.hiLevelDataFormat);
    returnText = returnText.replace("#measureunit", address.measureUnit);

    /**
     * Mauro (da mail): #value: deve essere tale e quale al numero generato dal gateway che lo ha letto dal sistema bus.
     *  Non avrà separatore delle migliaia e avrà tanti decimali quanti sono stati letti dal gateway;
     * quindi 0 se il numero è un numero intero. A video userà come separatore dei decimali
     * quello definito dalla lingua dell’utente. Se l’opzione UseThousandSeparatorForValueKeyword è True,
     *  allora apparirà anche il separatore delle migliaia, che sarà quello definito dalla lingua dell’utente.
     * Se si utilizza #decimaldigitsN allora le cifre decimali dovranno essere N indipendentemente dal fatto
     *  che in origine fossero di più, di meno o assenti (perché il numero era un intero).
     */
    if (returnText.indexOf("#value") >= 0) {
      if (
        address.hiLevelDataFormat ===
        EnumStringConverter.hiLevelDataFormatToStringEnum(
          HiLevelDataFormat.INTEGER
        ) ||
        address.hiLevelDataFormat ===
        EnumStringConverter.hiLevelDataFormatToStringEnum(
          HiLevelDataFormat.FLOAT
        )
      ) {
        if (decimalDigits === -1) {
          decimalDigits = this.countDecimal(address.lastValue);
        }
        let valueLabel = address.lastValue.toString().replace(",", ".");

        valueLabel = this._translatioNService.formatDecimal(
          parseFloat(valueLabel),
          this._useThousandSeparator,
          decimalDigits
        );
        returnText = returnText.replace("#value", valueLabel);
      } else {
        returnText = returnText.replace("#value", address.lastValue);
      }
    }

    /**
     * Mauro (da mail): #formattedvalue: deve essere #value visto in modo “bello”.
     * Quindi userà sempre il separatore delle migliaia (secondo la lingua dell’utente)
     * e un numero di cifre decimali definito dal setting a livello generale.
     * I decimali appaiono solo per i dati float. Se si utilizza #decimaldigitsN, vale il discorso precedente.
     */
    if (returnText.indexOf("#formattedvalue") >= 0) {
      if (
        (address.hiLevelDataFormat ===
          EnumStringConverter.hiLevelDataFormatToStringEnum(
            HiLevelDataFormat.INTEGER
          ) ||
          address.hiLevelDataFormat ===
          EnumStringConverter.hiLevelDataFormatToStringEnum(
            HiLevelDataFormat.FLOAT
          )) &&
        !isNaN(parseFloat(address.lastState))
      ) {
        let formattedValue = address.lastValue.toString().replace(",", ".");

        if (indexOfDecimalDigits >= 0) {
          decimalDigits = decimalDigits;
        } else if (
          address.hiLevelDataFormat ===
          EnumStringConverter.hiLevelDataFormatToStringEnum(
            HiLevelDataFormat.INTEGER
          )
        ) {
          decimalDigits = 0;
        } else if (decimalDigits === -1) {
          decimalDigits = this._defaultDecimailDigits;
        }

        formattedValue = this._translatioNService.formatDecimal(
          parseFloat(formattedValue),
          true,
          decimalDigits
        );

        returnText = returnText.replace("#formattedvalue", formattedValue);
      } else {
        returnText = returnText.replace("#formattedvalue", address.lastState);
      }
    }

    returnText = returnText.replace("#techvalue", address.lastTechValue);
    returnText = returnText.replace("#timestamp", address.lastValueTimestamp);
    returnText = returnText.replace(
      "#roomnumber",
      address.translatedNRoom || address.nRoom
    );
    returnText = returnText.replace(
      "#roomname",
      address.translatedRoomDescription || address.roomDescription
    );

    returnText = this.checkTrimKeyword(returnText);

    return returnText;
  }

  /**
   * This method handle the syncronisation of "time values" in this component's "text" property. It does so cycling on the enum of known time-related keyword; for each of them
   * that is ACTUALLY present inside the text, it checks if it is necessary to replace the current present value (IE. the "old value") with a newly-generated one; this happens
   * wnhe the two values are different.
   *
   * NOTE: Even if the method cycle each time on all the defined keywords, it actually does operations for all of them ONLY the first time (this in necessary in order to check
   * what keywords are present); from the second time onwards, it does work only for the present keywords, since the "oldValues" associated with the not-present keywords have been
   * set to empty string.
   *
   *
   * @param textValue
   * @returns
   */
  private replaceDateTimeKeywords(currentText: string): string {
    this._currentDateTimeKeywordUsed.map((keyword: string) => {
      currentText = this.replaceDateTimeTextByKeywordValue(
        currentText,
        keyword
      );
    });

    return currentText;
  }

  /**
   * Replace keyword text with specific keyword date time value
   *
   * @param currentText Current text
   * @param keyword keyword value
   * @returns
   */
  private replaceDateTimeTextByKeywordValue(
    currentText: string,
    keyword: string
  ): string {
    let formattedDateTime: string = "";
    const currentDateMoment = moment();

    switch (keyword) {
      case TimeRelatedKeywords.CLOCKSHORT:
        formattedDateTime = currentDateMoment.format("HH:mm");

        currentText = currentText.replace(
          new RegExp(TimeRelatedKeywords.CLOCKSHORT),
          formattedDateTime
        );
        break;
      case TimeRelatedKeywords.CLOCK:
        formattedDateTime = currentDateMoment.format("HH:mm:ss");

        currentText = currentText.replace(
          new RegExp(TimeRelatedKeywords.CLOCK),
          formattedDateTime
        );
        break;
      case TimeRelatedKeywords.HOURS2DIGITS:
        formattedDateTime = currentDateMoment.format("HH");

        currentText = currentText.replace(
          new RegExp(TimeRelatedKeywords.HOURS2DIGITS),
          formattedDateTime
        );
        break;
      case TimeRelatedKeywords.HOURS:
        formattedDateTime = currentDateMoment.format("H");

        currentText = currentText.replace(
          new RegExp(TimeRelatedKeywords.HOURS),
          formattedDateTime
        );
        break;
      case TimeRelatedKeywords.MINUTES2DIGITS:
        formattedDateTime = currentDateMoment.format("mm");

        currentText = currentText.replace(
          new RegExp(TimeRelatedKeywords.MINUTES2DIGITS),
          formattedDateTime
        );
        break;
      case TimeRelatedKeywords.MINUTES:
        formattedDateTime = currentDateMoment.format("m");

        currentText = currentText.replace(
          new RegExp(TimeRelatedKeywords.MINUTES),
          formattedDateTime
        );
        break;
      case TimeRelatedKeywords.SECONDS2DIGITS:
        formattedDateTime = currentDateMoment.format("ss");

        currentText = currentText.replace(
          new RegExp(TimeRelatedKeywords.SECONDS2DIGITS),
          formattedDateTime
        );
        break;
      case TimeRelatedKeywords.SECONDS:
        formattedDateTime = currentDateMoment.format("s");

        currentText = currentText.replace(
          new RegExp(TimeRelatedKeywords.SECONDS),
          formattedDateTime
        );
        break;
      case TimeRelatedKeywords.DATESHORT:
        formattedDateTime = currentDateMoment.format("DD:MM");

        currentText = currentText.replace(
          new RegExp(TimeRelatedKeywords.DATESHORT),
          formattedDateTime
        );
        break;
      case TimeRelatedKeywords.DATE:
        formattedDateTime = currentDateMoment.format("DD:MM:YYYY");

        currentText = currentText.replace(
          new RegExp(TimeRelatedKeywords.DATE),
          formattedDateTime
        );
        break;
      case TimeRelatedKeywords.DAY2DIGITS:
        formattedDateTime = currentDateMoment.format("DD");

        currentText = currentText.replace(
          new RegExp(TimeRelatedKeywords.DAY2DIGITS),
          formattedDateTime
        );
        break;
      case TimeRelatedKeywords.DAYNAMESHORT:
        formattedDateTime = currentDateMoment.format("dddd").substring(0, 3);

        currentText = currentText.replace(
          new RegExp(TimeRelatedKeywords.DAYNAMESHORT),
          formattedDateTime
        );
        break;
      case TimeRelatedKeywords.DAYNAME:
        formattedDateTime = currentDateMoment.format("dddd");

        currentText = currentText.replace(
          new RegExp(TimeRelatedKeywords.DAYNAME),
          formattedDateTime
        );
        break;
      case TimeRelatedKeywords.DAY:
        formattedDateTime = currentDateMoment.format("D");

        currentText = currentText.replace(
          new RegExp(TimeRelatedKeywords.DAY),
          formattedDateTime
        );
        break;
      case TimeRelatedKeywords.MONTH2DIGITS:
        formattedDateTime = currentDateMoment.format("MM");

        currentText = currentText.replace(
          new RegExp(TimeRelatedKeywords.MONTH2DIGITS),
          formattedDateTime
        );
        break;
      case TimeRelatedKeywords.MONTHNAMESHORT:
        formattedDateTime = currentDateMoment.format("MMMM").substring(0, 3);

        currentText = currentText.replace(
          new RegExp(TimeRelatedKeywords.MONTHNAMESHORT),
          formattedDateTime
        );
        break;
      case TimeRelatedKeywords.MONTHNAME:
        formattedDateTime = currentDateMoment.format("MMMM");

        currentText = currentText.replace(
          new RegExp(TimeRelatedKeywords.MONTHNAME),
          formattedDateTime
        );
        break;
      case TimeRelatedKeywords.MONTH:
        formattedDateTime = currentDateMoment.format("M");

        currentText = currentText.replace(
          new RegExp(TimeRelatedKeywords.MONTH),
          formattedDateTime
        );
        break;
      case TimeRelatedKeywords.YEAR2DIGITS:
        formattedDateTime = currentDateMoment.format("YY");

        currentText = currentText.replace(
          new RegExp(TimeRelatedKeywords.YEAR2DIGITS),
          formattedDateTime
        );
        break;
      case TimeRelatedKeywords.YEAR:
        formattedDateTime = currentDateMoment.format("YYYY");

        currentText = currentText.replace(
          new RegExp(TimeRelatedKeywords.YEAR),
          formattedDateTime
        );
        break;
    }

    return currentText;
  }

  /**
   * Check if the value contains a keyword AB:out date time
   * @param value
   */
  private valueContainsDateTimeKeywords(value: string) {
    return (
      value.includes("#clockshort") ||
      value.includes("#clock") ||
      value.includes("#dateshort") ||
      value.includes("#date") ||
      value.includes("#hours2digits") ||
      value.includes("#minutes2digits") ||
      value.includes("#seconds2digits") ||
      value.includes("#hours") ||
      value.includes("#minutes") ||
      value.includes("#seconds") ||
      value.includes("#daynameshort") ||
      value.includes("#dayname") ||
      value.includes("#monthnameshort") ||
      value.includes("#monthname") ||
      value.includes("#day2digits") ||
      value.includes("#month2digits") ||
      value.includes("#year2digits") ||
      value.includes("#day") ||
      value.includes("#month") ||
      value.includes("#year")
    );
  }

  /**
   * Load current datetime keyword by nested enum
   */
  private loadDateTimeKeywordsKey(): void {
    this._currentDateTimeKeywordUsed = Object.keys(this._timeRelatedKeywords)
      .filter((keyword: string) =>
        this.isDateTimeKeywordInOriginalString(keyword)
      )
      .map((keyword: string) => this._timeRelatedKeywords[keyword]);
  }

  /**
   * Check if specific datetime kwyword is in original text
   *
   * @param keyword Keyword to test
   * @returns
   */
  private isDateTimeKeywordInOriginalString(keyword: string): boolean {
    return this._originalText.search(new RegExp(keyword, "i")) > -1;
  }

  /**
   * handle trim keyword handling
   *
   * @param currentText  Current text to check trim value
   * @returns Formatted text
   */
  private checkTrimKeyword(currentText: string): string {
    let trimValueCharLeft: number = 0;
    let trimValueCharRight: number = 0;

    if (currentText.indexOf("#trimleft") >= 0) {
      const tmp = currentText.split(",");
      trimValueCharLeft = parseInt(tmp[1].replace(")", ""), 10);

      currentText = currentText.replace("#trimleft(", "");
      currentText = currentText.replace("," + trimValueCharLeft + ")", "");
      currentText = currentText.substring(
        trimValueCharLeft,
        currentText.length
      );
    }

    if (currentText.indexOf("#trimright") >= 0) {
      const tmp = currentText.split(",");
      trimValueCharRight = parseInt(tmp[1].replace(")", ""), 10);

      currentText = currentText.replace("#trimright(", "");
      currentText = currentText.replace("," + trimValueCharRight + ")", "");
      currentText = currentText.substring(
        0,
        currentText.length - trimValueCharRight
      );
    }

    return currentText;
  }

  /**
   * Count decimal in float double number
   *
   * @param value Intiial value
   * @returns
   */
  private countDecimal(value: string): number {
    const valueSplit: string[] = value.split(".");
    if (valueSplit.length === 1) {
      return 0;
    } else {
      return valueSplit[1].length;
    }
  }
}
